import { serverTimestamp } from 'firebase/firestore';

/**
 * Fee Receipt Model
 * Represents a fee receipt record from Firestore
 */
export class FeeReceipt {
    constructor({
        Class,
        academicYear = '',
        admissionNo = '',
        data = null,
        date = null,
        receiptType = '',
        sec = '',
        totalPaid = 0,
        userId = '',
        voucherNo = '',
        id = null,
    } = {}) {
        this.Class = Class;
        this.academicYear = academicYear;
        this.admissionNo = admissionNo;
        this.data = data; // any (map)
        this.date = date; // Will be converted to JS Date in fromMap
        this.receiptType = receiptType;
        this.sec = sec;
        this.totalPaid = parseFloat(totalPaid) || 0;
        this.userId = userId;
        this.voucherNo = voucherNo;
        this.id = id;
    }

    /**
     * Creates a FeeReceipt instance from Firestore document data
     * @param {Object} data - Firestore document data
     * @param {string} docId - Document ID
     * @returns {FeeReceipt} FeeReceipt instance
     */
    static fromMap(data, docId = null) {
        if (!data) {
            return new FeeReceipt();
        }

        // Convert Firestore Timestamp to JavaScript Date
        let dateValue = null;
        if (data.date) {
            // Check if it's a Firestore Timestamp
            if (data.date.toDate && typeof data.date.toDate === 'function') {
                dateValue = data.date.toDate();
            } else if (data.date instanceof Date) {
                dateValue = data.date;
            } else if (typeof data.date === 'string') {
                dateValue = new Date(data.date);
            }
        }

        return new FeeReceipt({
            Class: data.Class,
            academicYear: data.academicYear,
            admissionNo: data.admissionNo,
            data: data.data,
            date: dateValue,
            receiptType: data.receiptType,
            sec: data.sec,
            totalPaid: data.totalPaid,
            userId: data.userId,
            voucherNo: data.voucherNo,
            id: docId,
        });
    }

    /**
     * Converts the FeeReceipt instance to a plain object for Firestore
     * @returns {Object} Plain object representation
     */
    toMap() {
        return {
            Class: this.Class,
            academicYear: this.academicYear,
            admissionNo: this.admissionNo,
            data: this.data,
            date: serverTimestamp(),
            receiptType: this.receiptType,
            sec: this.sec,
            totalPaid: this.totalPaid,
            userId: this.userId,
            voucherNo: this.voucherNo,
        };
    }

    /**
     * Gets formatted total paid amount with currency symbol
     * @returns {string} Formatted amount
     */
    getFormattedTotalPaid() {
        return `₹${this.totalPaid.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    /**
     * Gets formatted date string
     * @returns {string} Formatted date
     */
    getFormattedDate() {
        if (!this.date) return 'N/A';
        return this.date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    /**
     * Gets class and section display string
     * @returns {string} Class and section
     */
    getClassSection() {
        return this.sec ? `${this.class}-${this.sec}` : `${this.class}`;
    }

    /**
     * Gets student identifier (admission number or name from data)
     * @returns {string} Student identifier
     */
    getStudentIdentifier() {
        if (this.admissionNo) {
            return this.admissionNo;
        }
        
        // Try to extract name from data if available
        if (this.data && typeof this.data === 'object') {
            if (this.data.studentName) return this.data.studentName;
            if (this.data.name) return this.data.name;
        }
        
        return 'N/A';
    }

    /**
     * Gets receipt type display string
     * @returns {string} Receipt type
     */
    getReceiptTypeDisplay() {
        if (!this.receiptType) return 'N/A';
        
        // Convert camelCase or snake_case to proper case
        return this.receiptType
            .replace(/([A-Z])/g, ' $1')
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase())
            .trim();
    }
}



export default FeeReceipt;
